import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Modal,
  Snackbar,
  Alert,
  IconButton,
} from '@mui/material';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import routes from '../../routes/routes';
import { useFindProduct } from '../../hooks/useFindProduct';
import ProductImage from '../ui/ProductImage';
import { useWindowSize } from '../../hooks/useWindowSize';
import { useBarcodeScannerWaiting } from '../../hooks/useBarcodeScannerWaiting';
import { config } from '../../config/config';
import { formatMoney, formatWeight } from '../../helpers/dataFormatter';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SearchIcon from '@mui/icons-material/Search';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  terminalBarIconButtonSx,
  terminalColors,
} from '../../theme/terminalStyles';
import BarcodeCameraDialog from '../picking/BarcodeCameraDialog';
import ProductPickerDialog from '../stocktake/ProductPickerDialog';

const pageRootSx = {
  height: '100%',
  minHeight: 0,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  mx: { xs: -1.5, sm: -2 },
  px: { xs: 1.5, sm: 2 },
  background: `linear-gradient(180deg, ${terminalColors.gradientTop} 0%, ${terminalColors.gradientBottom} 100%)`,
};

const contentCardSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: 2,
  bgcolor: terminalColors.cardWhite,
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  boxSizing: 'border-box',
};

const NumericKeyboardModal = ({ open, handleClose, handleSearch, onRequestCamera }) => {
  const [inputData, setInputData] = useState('');
  const keyboardElement = useRef();

  const handleKeyboardInput = (inputValue) => {
    setInputData(inputValue);
  };

  const applyInput = () => {
    clearInput();
    handleSearch(inputData);
  };

  const clearInput = () => {
    setInputData('');
    if (keyboardElement.current) {
      keyboardElement.current.clearInput();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 400,
          bgcolor: terminalColors.gradientTop,
          boxShadow: 24,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Введіть штрихкод товару
        </Typography>

        <TextField
          fullWidth
          variant="outlined"
          value={inputData}
          onChange={(e) => setInputData(e.target.value)}
          margin="normal"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: terminalColors.cardWhite } }}
        />

        <Keyboard
          keyboardRef={(r) => {
            keyboardElement.current = r;
          }}
          onChange={handleKeyboardInput}
          layout={{
            default: ['1 2 3', '4 5 6', '7 8 9', '{clearAll} 0 {bksp}'],
          }}
          display={{
            '{bksp}': '⌫',
            '{clearAll}': 'C',
          }}
          theme="hg-theme-default hg-layout-numeric"
          buttonTheme={[
            {
              class: 'hg-highlight',
              buttons: '0 1 2 3 4 5 6 7 8 9',
            },
          ]}
          onKeyPress={(button) => {
            if (button === '{clearAll}') {
              clearInput();
            }
          }}
        />

        <TerminalOrangeButton
          fullWidth
          sx={{ mt: 1.5 }}
          startIcon={<PhotoCameraIcon />}
          onClick={() => {
            handleClose();
            onRequestCamera?.();
          }}
        >
          Сканувати з камери
        </TerminalOrangeButton>
        <TerminalOrangeButton
          fullWidth
          sx={{ mt: 1 }}
          startIcon={<SearchIcon />}
          onClick={applyInput}
          disabled={!inputData}
        >
          Пошук
        </TerminalOrangeButton>
      </Box>
    </Modal>
  );
};

const EmptyDataScan = () => (
  <Box
    sx={{
      ...contentCardSx,
      alignItems: 'center',
      justifyContent: 'center',
      px: 2,
      py: { xs: 2, sm: 3 },
      gap: { xs: 1.5, sm: 2 },
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: { xs: 140, sm: 180 },
        height: { xs: 140, sm: 180 },
        borderRadius: '50%',
        bgcolor: 'rgba(255, 179, 71, 0.22)',
        border: `3px solid ${terminalColors.accentOrange}`,
        flexShrink: 0,
      }}
    >
      <QrCodeScannerIcon
        sx={{
          fontSize: { xs: 88, sm: 108 },
          color: terminalColors.accentOrangeHover,
        }}
        aria-hidden
      />
    </Box>
    <Typography
      variant="h5"
      align="center"
      sx={{
        fontWeight: 700,
        color: terminalColors.textPrimary,
        fontSize: { xs: '1.35rem', sm: '1.75rem' },
        lineHeight: 1.25,
        px: 1,
      }}
    >
      Відскануйте товар
    </Typography>
    {/* <Typography
      variant="body1"
      align="center"
      color="text.secondary"
      sx={{
        fontSize: { xs: '0.95rem', sm: '1.05rem' },
        maxWidth: 320,
        px: 1,
      }}
    >
      Наведіть сканер на штрихкод або скористайтесь клавіатурою / камерою зверху
    </Typography> */}
  </Box>
);

const formatArticle = (product) => {
  const a = product?.article;
  if (a == null || String(a).trim() === '') return '—';
  return String(a).trim();
};

const ProductData = ({ productData }) => {
  const showWeight = productData?.isWeightProduct && productData?.quantityNow !== 1;
  const weightText = showWeight ? formatWeight(productData?.quantityNow) : '';
  const unitType = productData?.isWeightProduct ? 'кг' : 'шт';
  const article = formatArticle(productData);
  const productImage = productData?.imageUrl ?? productData?.image;

  return (
    <Box
      sx={{
        ...contentCardSx,
        p: { xs: 1.5, sm: 3 },
        gap: { xs: 1, sm: 2 },
      }}
    >
      <Box sx={{ flexShrink: 0, textAlign: 'center', px: 0.5 }}>
        <Typography
          align="center"
          sx={{
            fontWeight: 600,
            color: terminalColors.textPrimary,
            fontSize: { xs: '1.55rem', sm: '2rem', md: '2.4rem' },
            lineHeight: 1.2,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: { xs: 3, sm: 4 },
            WebkitBoxOrient: 'vertical',
          }}
        >
          {productData?.name}
        </Typography>
        <Typography
          align="center"
          sx={{
            mt: 0.75,
            color: 'text.secondary',
            fontSize: { xs: '1.1rem', sm: '1.15rem' },
            lineHeight: 1.3,
          }}
        >
          Артикул: <Box component="span" sx={{ fontWeight: 700, color: terminalColors.textPrimary }}>{article}</Box>
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1, sm: 2 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: { xs: '0 0 auto', md: 1 },
            textAlign: 'center',
            minWidth: 0,
          }}
        >
          {productData?.isSalePrice ? (
            <Typography
              color="text.secondary"
              sx={{
                textDecoration: 'line-through',
                fontSize: { xs: '2.15rem', sm: '3rem', md: '4rem' },
                fontWeight: 'bold',
                lineHeight: 1.1,
              }}
            >
              {formatMoney(productData?.priceUserOld ?? 0)} ₴ / {weightText} {unitType}
            </Typography>
          ) : null}
          <Typography
            sx={{
              color: productData?.isSalePrice ? 'error.main' : '#185592',
              fontSize: { xs: '2.65rem', sm: '3.2rem', md: '4.5rem' },
              fontWeight: 'bold',
              lineHeight: 1.1,
            }}
          >
            {formatMoney(productData?.priceUser ?? 0)} ₴ / {weightText} {unitType}
          </Typography>
        </Box>

        <ProductImage
          image={productImage}
          sx={{
            flex: { xs: '1 1 auto', md: 1 },
            width: { xs: '100%', md: '50%' },
            maxWidth: '100%',
            maxHeight: { xs: '38vh', sm: '45vh', md: '55vh' },
            minHeight: 0,
          }}
          placeholderSx={{
            flex: { xs: '1 1 auto', md: 1 },
            maxHeight: { xs: '32vh', sm: '40vh', md: '50vh' },
            my: { xs: 0, md: 0 },
          }}
        />
      </Box>
    </Box>
  );
};

const PriceChecker = () => {
  const { isNonMobile } = useWindowSize();
  const { productData, findProductByBarcode, findProductById, resNotFound, setResNotFound } =
    useFindProduct();
  const [cameraOpen, setCameraOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const scannerPaused = cameraOpen || catalogOpen;
  useBarcodeScannerWaiting(config.scanCodeLength, !scannerPaused, [scannerPaused], {
    mthd: findProductByBarcode,
  });
  const [dKeyboardModalOpen, setDKeyboardModalOpen] = useState(false);

  const handleSearch = (inputData) => {
    setDKeyboardModalOpen(false);
    findProductByBarcode(inputData);
  };

  const handleCatalogSelect = async (product) => {
    setCatalogOpen(false);
    if (!product?.id) return;
    const barcode = String(product.barcode || '').trim();
    if (barcode) {
      await findProductByBarcode(barcode);
      return;
    }
    await findProductById(product.id);
  };

  const searchToolbarActions = (
    <>
      <IconButton
        aria-label="пошук товару"
        onClick={() => setCatalogOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <SearchIcon />
      </IconButton>
      <IconButton
        aria-label="штрихкод"
        onClick={() => setDKeyboardModalOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <KeyboardIcon />
      </IconButton>
      <IconButton
        aria-label="камера"
        onClick={() => setCameraOpen(true)}
        sx={terminalBarIconButtonSx}
      >
        <PhotoCameraIcon />
      </IconButton>
    </>
  );

  const handleSnackbarClose = () => {
    setResNotFound(false);
  };

  return (
    <TerminalLayout
      title="Перевірка цін"
      titleIcon={<SellOutlinedIcon />}
      backTo={routes.root}
      endActions={searchToolbarActions}
    >
      <Box sx={pageRootSx}>
        {productData?.name ? (
          <ProductData productData={productData} />
        ) : (
          <EmptyDataScan />
        )}
      </Box>

      <BarcodeCameraDialog
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCode={(text) => {
          findProductByBarcode(text);
          setResNotFound(false);
        }}
        title="Сканувати штрихкод товару"
      />
      <NumericKeyboardModal
        open={dKeyboardModalOpen}
        handleClose={() => setDKeyboardModalOpen(false)}
        handleSearch={handleSearch}
        onRequestCamera={() => setCameraOpen(true)}
      />
      <ProductPickerDialog
        open={catalogOpen}
        standalone
        title="Пошук товару"
        onClose={() => setCatalogOpen(false)}
        onSelectProduct={handleCatalogSelect}
      />
      <Snackbar
        open={resNotFound}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{
            fontSize: isNonMobile ? '1.25rem' : '1rem',
            fontWeight: 600,
          }}
        >
          Товар не знайдено
        </Alert>
      </Snackbar>
    </TerminalLayout>
  );
};

export default PriceChecker;
